import ExpiredDomainsGrid from "components/expiredDomainsGrid/ExpiredDomainsGrid";
import InfoText from "components/infoText/InfoText";
import Page from "components/page/Page";
import PageTitle from "components/pageTitle/PageTitle";
import PageWrapper from "components/pageWrapper/PageWrapper";
import { useAppDispatch, useAppSelector } from "hooks";
import { memo, useEffect, useMemo, useCallback } from "react";
import { Alert } from "react-bootstrap";
import Helmet from "components/headHelmet/HeadHelmet";
import { useSearchParams } from "react-router-dom";
import domainSlice from "reducers/domainSlice";
import {
  expiredDomainsSelector,
  getExpiredDomainsErrorSelector,
  gettingExpiredDomainsSelector,
  expiredDomainsTotalSelector,
  expiredDomainsPageSelector,
  expiredDomainsPageSizeSelector,
} from "selectors/domainSelectors";
import { RootState } from "store";
import { getSiteUrl, isPrerendering } from "utility/urlUtility";
import { GridPageChangeEvent } from "@progress/kendo-react-grid";
import "@progress/kendo-theme-default/dist/all.css";
import { useTranslation } from "react-i18next";

const ExpiredDomainsPage = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("expired");
  const [searchParams, setSearchParams] = useSearchParams();

  const gettingExpiredDomains = useAppSelector((state: RootState) =>
    gettingExpiredDomainsSelector(state),
  );

  const expiredDomains = useAppSelector((state: RootState) =>
    expiredDomainsSelector(state),
  );

  const total = useAppSelector((state: RootState) =>
    expiredDomainsTotalSelector(state),
  );

  const page = useAppSelector((state: RootState) =>
    expiredDomainsPageSelector(state),
  );

  const pageSize = useAppSelector((state: RootState) =>
    expiredDomainsPageSizeSelector(state),
  );

  const getExpiredDomainsError = useAppSelector((state: RootState) =>
    getExpiredDomainsErrorSelector(state),
  );

  useEffect(() => {
    // Skip API calls during pre-rendering
    if (isPrerendering()) {
      return;
    }

    // Read page from URL
    const urlPage = parseInt(searchParams.get("page") || "1", 10);
    const validPage = urlPage > 0 ? urlPage : 1;

    dispatch(domainSlice.actions.getExpiredDomains({ page: validPage }));
  }, [dispatch, searchParams]);

  const handlePageChange = useCallback(
    (event: GridPageChangeEvent) => {
      const newPage = Math.floor(event.page.skip / event.page.take) + 1;

      // Update URL with new page number
      setSearchParams({ page: newPage.toString() });
    },
    [setSearchParams],
  );

  const pageUrl = useMemo(() => {
    const currentPage = parseInt(searchParams.get("page") || "1", 10);
    const baseUrl = `${getSiteUrl()}/expired-domains`;
    return currentPage > 1 ? `${baseUrl}?page=${currentPage}` : baseUrl;
  }, [searchParams]);

  const totalPages = useMemo(() => {
    return Math.ceil(total / pageSize);
  }, [total, pageSize]);

  const currentPage = useMemo(() => {
    return parseInt(searchParams.get("page") || "1", 10);
  }, [searchParams]);

  const metaTitle = t("metaTitle");
  const metaDescription = t("metaDescription");

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href={pageUrl} />

        {/* Pagination meta tags for SEO */}
        {currentPage > 1 && (
          <link
            rel="prev"
            href={`${getSiteUrl()}/expired-domains?page=${currentPage - 1}`}
          />
        )}
        {currentPage < totalPages && (
          <link
            rel="next"
            href={`${getSiteUrl()}/expired-domains?page=${currentPage + 1}`}
          />
        )}

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content={metaTitle} />
        <meta property="twitter:description" content={metaDescription} />
      </Helmet>
      <Page collapsedView={true}>
        <PageWrapper>
          <PageTitle>{t("pageTitle")}</PageTitle>

          <InfoText>{t("infoText")}</InfoText>

          {getExpiredDomainsError && (
            <Alert variant="danger">{getExpiredDomainsError}</Alert>
          )}

          {!getExpiredDomainsError && (
            <ExpiredDomainsGrid
              data={expiredDomains || []}
              total={total}
              page={page}
              pageSize={pageSize}
              loading={gettingExpiredDomains}
              onPageChange={handlePageChange}
            />
          )}
        </PageWrapper>
      </Page>
    </>
  );
};

export default memo(ExpiredDomainsPage);
